import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import { ArrowLeft, Users, QrCode } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function StudentJoin() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const initialCode = (params.get("code") || "").toUpperCase();
  const [code, setCode] = useState(initialCode);
  const [name, setName] = useState("");
  const [rollNumber, setRollNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoCode, setAutoCode] = useState(!!initialCode);
  const nav = useNavigate();

  // RODO-friendly: only first name required (no surname).
  const trimmedName = name.trim();
  const nameValid = /^\p{L}[\p{L}\-']{0,59}$/u.test(trimmedName);
  const nameTouched = trimmedName.length > 0;
  const rollNum = parseInt(rollNumber, 10);
  const rollValid = Number.isInteger(rollNum) && rollNum >= 1 && rollNum <= 40;
  const rollTouched = rollNumber.length > 0;
  const canSubmit = code.trim().length === 6 && nameValid && rollValid && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!nameValid) {
      toast.error("Podaj imię (bez nazwiska)");
      return;
    }
    if (!rollValid) {
      toast.error("Podaj numer w dzienniku (1–40)");
      return;
    }
    setLoading(true);
    try {
      const upper = code.trim().toUpperCase();
      // Point 5 — anti-duplicate: if this device already joined this session,
      // reuse the existing student_id and drop the student straight back into
      // their quiz instead of creating another empty submission.
      let existingId = null;
      try {
        existingId = localStorage.getItem(`join:${upper}`);
      } catch { /* localStorage unavailable — ignore */ }
      if (existingId) {
        try {
          const check = await publicApi.get(`/public/sessions/${upper}/result/${existingId}`);
          if (check?.data?.submission) {
            toast.success("Wracasz do swojej rozpoczętej pracy 👋");
            nav(`/quiz/${upper}/${existingId}`);
            return;
          }
        } catch {
          // stale entry — remove it and continue with a fresh join
          try { localStorage.removeItem(`join:${upper}`); } catch { /* noop */ }
        }
      }
      await publicApi.get(`/public/sessions/${upper}`);
      const { data } = await publicApi.post(
        `/public/sessions/${upper}/join`,
        { name: trimmedName, roll_number: rollNum },
      );
      try { localStorage.setItem(`join:${upper}`, data.student_id); }
      catch { /* private mode — ignore */ }
      toast.success(t("student.toast_joined"));
      nav(`/quiz/${data.code}/${data.student_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("student.toast_join_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E0F2FE]">
      <header className="border-b-2 border-[#0F172A] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
            <span>{t("student.back")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B] hidden sm:block">
              {t("student.header_join_quiz")}
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="inline-flex items-center gap-2 brut bg-[#FEF08A] px-3 py-1 rounded-md mb-6">
          <Users className="w-3 h-3" strokeWidth={3} />
          <span className="text-xs tracking-[0.2em] uppercase font-bold">
            {t("student.badge_student_mode")}
          </span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mb-2">
          {t("student.join_title")}
        </h1>
        <p className="text-[#334155] mb-8 max-w-md">
          {autoCode ? t("student.join_desc_qr") : t("student.join_desc_manual")}
        </p>

        <form onSubmit={submit} className="brut bg-white rounded-md p-6 space-y-5 max-w-lg">
          <div>
            <Label htmlFor="code" className="font-bold text-sm flex items-center gap-2">
              {t("student.label_code")}
              {autoCode && (
                <span className="inline-flex items-center gap-1 brut bg-[#D1FAE5] px-2 py-0.5 rounded-md text-[10px] tracking-[0.15em] uppercase">
                  <QrCode className="w-3 h-3" strokeWidth={3} />{t("student.label_from_qr")}
                </span>
              )}
            </Label>
            <Input
              id="code"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setAutoCode(false); }}
              className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-14 font-display font-black text-2xl tracking-[0.3em] text-center uppercase"
              placeholder="ABC123"
              maxLength={6}
              autoFocus={!autoCode}
              data-testid="student-code-input"
            />
          </div>
          <div>
            <Label htmlFor="name" className="font-bold text-sm">
              Imię <span className="text-[#64748B] font-normal">(bez nazwiska — RODO)</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-12"
              placeholder="Anna"
              autoFocus={autoCode}
              data-testid="student-name-input"
            />
            {nameTouched && !nameValid && (
              <p className="text-xs font-bold text-[#B91C1C] mt-1.5"
                 data-testid="student-name-hint">
                Podaj samo imię (bez nazwiska).
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="roll" className="font-bold text-sm">
              Numer w dzienniku <span className="text-[#64748B] font-normal">(1–40)</span>
            </Label>
            <Input
              id="roll"
              type="number"
              inputMode="numeric"
              min={1}
              max={40}
              value={rollNumber}
              onChange={(e) => setRollNumber(e.target.value.replace(/\D/g, "").slice(0, 2))}
              className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-12 font-display font-black text-xl text-center"
              placeholder="12"
              data-testid="student-roll-input"
            />
            {rollTouched && !rollValid && (
              <p className="text-xs font-bold text-[#B91C1C] mt-1.5"
                 data-testid="student-roll-hint">
                Numer musi być liczbą 1–40.
              </p>
            )}
          </div>
          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md h-12 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="join-session-btn"
          >
            {loading ? t("student.joining") : t("student.join_button")}
          </Button>
        </form>
      </div>
    </div>
  );
}
